import * as React from 'react';
import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import debounce from 'lodash/debounce';

import {SelectReact} from 'common/components/select';
import {SvgIconReact} from 'common/components/svg-icon';
import {ButtonBarReact} from 'common/components/button-bar';
import {ButtonBarTheme} from 'common/components/button-bar/types';
import {useTranslation} from 'common/i18n/hooks';
import {WorkspaceObj, workspacesClient} from 'common/report/queries/app-api/workspaces-client';
import {Project} from 'common/report/queries/app-api/project-metadata-client';
import {useProjects} from 'common/contexts/react/projects-context';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {useCurrentUser} from 'common/contexts/react/current-user-context';
import {excludeDemos, getReportUrl, isPersistableReport} from 'common/report/util';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {DisplaySize, Placement, TooltipTextAlign} from 'common/components/tooltip/types';
import {openDemoProjectSelectorModal} from 'common/widgets/demo-selector-modal';
import {ToastType} from 'common/components/toast/types';
import {Organization} from 'common/report/stores/user-details';
import {pluralize, truncateMiddle} from 'common/util/string';
import {
  ApiFailureDescription,
  UISourceObject,
  workspaceApiFailure,
  workspaceCreateEvent,
  workspacesTrackingPrefix,
} from 'common/util/workspaces';
import {addToast} from 'common/components/toast-area/toast-manager';
import {makePathFromUrlState} from 'common/report/util/state-from-url';
import {Permission, ProjectType} from 'common/report/constants';
import {reporter} from 'common/report/error-reporting';
import {promptManageWorkspaceModal} from 'common/widgets/create-entity-modal';
import {ToastPortalReact} from 'common/components/toast-portal';
import {getLocation} from 'common/util/url';
import {ChangeEvent, MenuChangeEvent, SearchFilterChangeEvent} from 'common/components/select/types';
import {shouldOpenInNewTab} from 'common/util/dom';
import {SelectTheme} from 'common/components/select/constants';
import {Directions} from 'common/components/drop-menu/types';
import {
  getCurrentWorkspaceId,
  getFilteredOrgs,
  getWorkspacesForCurrentProject,
  getWorkspacesForProject,
} from 'iron/routes/global-sidenav/utils';
import {ProjectSwitcherProps, TrackingEvent} from 'iron/routes/global-sidenav/widgets/project-switcher/types';
import {withStyles} from 'common/util/react';

import styles from './index.module.styl';
import css from './index.module.styl?inline';

export const ProjectSwitcher: React.FC<ProjectSwitcherProps> = withStyles((props) => {
  const i18n = useTranslation();
  const projectsResource = useProjects();
  const currentProjectResource = useCurrentProject();
  const currentProject = currentProjectResource.get().value;
  const currentUser = useCurrentUser().get().value;

  const [selectedProjectWorkspaces, setSelectedProjectWorkspaces] = React.useState<WorkspaceObj[]>([]);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [errorAlertMsg, setErrorAlertMsg] = React.useState<string>();
  const [projectSearchFilter, setProjectSearchFilter] = React.useState(``);

  function mixpanelTrack(
    event: TrackingEvent | string,
    properties: {[key: string]: any} = {},
    {prefix = `[Project Switcher]`}: {prefix?: string} = {},
  ) {
    const eventName = prefix ? `${prefix} ${event}` : event;
    mixpanel.track(eventName, {...properties});
  }

  function areWorkspacesActivated(projectId?: number): boolean {
    const projectWorkspaces = !projectId
      ? selectedProjectWorkspaces
      : getWorkspacesForProject({projectsResource, currentProject, projectId});

    return (
      projectWorkspaces.length > 1 ||
      (projectWorkspaces.length === 1 &&
        Boolean(projectsResource.getOne(projectWorkspaces[0].projectId).value?.hasWorkspaces))
    );
  }

  function getAllProjects(): Project[] {
    return projectsResource.getAllValues();
  }

  function getProjectsForMenu(): Project[] {
    return excludeDemos(getAllProjects());
  }

  function getOrgs({
    permissions,
    excludeDemoOrgs,
  }: {permissions?: Permission[]; excludeDemoOrgs?: boolean} = {}): Organization[] {
    return getFilteredOrgs(currentUser?.organizations ?? {}, {permissions, excludeDemoOrgs});
  }

  function getProjectItems(projects: Project[]): SectionItem[] {
    return projects
      .map((proj) => {
        const workspaceCount = getWorkspacesForProject({projectsResource, currentProject, projectId: proj.id}).length;
        const workspaceOverrides = areWorkspacesActivated(proj.id)
          ? {
              subLabel: `${workspaceCount} ${pluralize(`Data View`, workspaceCount)}`,
              hasCaret: workspaceCount > 0,
            }
          : {};

        return {
          label: proj.name || `My New Project`,
          subLabel: proj.isRollupProject() && getOrgs().length > 1 ? i18n.t(`nav-header.rollup-projects`) : ``,
          projectId: proj.id,
          type: proj.type,
          ...workspaceOverrides,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  function getMenuSections(): Section[] {
    let orgList: Organization[];
    const allProjects = getProjectsForMenu();
    let sections: Section[];

    if (areWorkspacesActivated()) {
      sections = [
        {
          items: selectedProjectWorkspaces.map((workspace) => ({
            label: workspace.name,
            id: workspace.id,
            isWorkspace: true,
            projectId: workspace.projectId,
            tooltip: {
              text: `${workspace.name}\n${i18n.t(`nav-header.data-view-created-by`, {
                creator: workspace.creator,
              })}\n\n${workspace.description}\n[${i18n.t(
                `nav-header.data-view-more-details`,
              )}](/settings/project/${workspace.projectId}/views/detail/${workspace.id})`,
              size: DisplaySize.Medium,
            },
          })),
        },
        {
          items: [
            {
              label: i18n.t(`nav-header.data-view-library`),
              icon: `open`,
              projectId: selectedProjectWorkspaces[0].projectId,
              href: getReportUrl(`views`, {projectId: selectedProjectWorkspaces[0].projectId}),
            },
          ],
        },
      ];
    } else if ((orgList = getOrgs({excludeDemoOrgs: true})).length > 1) {
      sections = orgList.map((org) => ({
        label: orgList.length > 1 ? org.name : undefined,
        items: getProjectItems(allProjects.filter((p: Project) => p.organizationId === org.id)),
      }));
    } else {
      const projects = allProjects.filter((p) => !p.isRollupProject());
      const rollupProjects = allProjects.filter((p) => p.isRollupProject());
      const hasRollupProjects = rollupProjects.length > 0;
      sections = [
        {
          // Only explicitly label if there are rollup projects, otherwise its just a single list
          label: hasRollupProjects ? i18n.t(`nav-header.projects`) : ``,
          items: getProjectItems(projects),
        },
      ];

      if (hasRollupProjects) {
        sections.push({
          label: i18n.t(`nav-header.rollup-projects`),
          items: getProjectItems(rollupProjects),
        });
      }
    }
    if (!areWorkspacesActivated()) {
      sections.push({
        label: i18n.t(`nav-header.mixpanel-demo`),
        items: [
          {
            label: i18n.t(`nav-header.sample-datasets`),
            icon: `open`,
            projectId: 0,
            onClick: () => {
              openDemoProjectSelectorModal((ev) => {
                addToast({message: ev.detail.message, type: ToastType.ERROR});
              });
            },
            trackEvent: `[Demo Project] Click Explore Demo`,
            trackProps: {'Navigated From': `Project Menu`},
          },
        ],
      });
    }

    // cap sections to max of 10 items so list isn't insanely long
    for (const section of sections) {
      section.showLimit = section.items!.length > 10 ? 5 : 10;
    }

    return sections;
  }

  function handleBackClick(): void {
    mixpanelTrack(TrackingEvent.BackToProjectsClicked);
    setSelectedProjectWorkspaces([]);
  }

  function navigateTo(url: string, ev?: CustomEvent, {fromAnchorlink = false} = {}): void {
    if (
      ev &&
      shouldOpenInNewTab(ev) &&
      // if anchor link, browser will do the right thing by opening in new tab anyway, so don't need to manually
      // open new tab here
      !fromAnchorlink
    ) {
      window.open(url, `_blank`);
    } else {
      // TODO: Should eventually switch to use react router's useNavigate, but can't yet because the
      // app doesn't correctly refresh data when switching workspace/project.
      window.location.assign(url);
    }
  }

  function navigateToProject(ev: CustomEvent): void {
    const item = ev.detail.item as SectionItem;
    const projectId = item.projectId;
    const projectDomain = currentProject?.domain;
    const projectWorkspaces = getWorkspacesForProject({projectsResource, currentProject, projectId});
    const workspaceId = item.isWorkspace ? item.id : get(projectWorkspaces[0], `id`);
    const location = getLocation();

    if (item.hasCaret && projectWorkspaces.length) {
      setSelectedProjectWorkspaces(projectWorkspaces);
      return;
    }

    setIsMenuOpen(false);
    setSelectedProjectWorkspaces([]);

    // e.g if user is in insights, navigate to insights in new project or workspace
    let navigatePath = makePathFromUrlState({projectId, workspaceId});

    // if switching between workspaces for the same project, preserve the hash for reports to keep selected user report
    if (
      navigatePath &&
      `${workspaceId}` !== `${getCurrentWorkspaceId({currentProject})}` &&
      `${projectId}` === `${currentProject?.id}`
    ) {
      navigatePath += location.hash;
    }

    if (!isPersistableReport() || !navigatePath) {
      // let django navigate to last persisted report
      // trailing slashes are important otherwise we get an extra 301 roundtrip
      navigatePath = workspaceId ? `/report/${projectId}/view/${workspaceId}/` : `/report/${projectId}/`;
    }

    if (workspaceId) {
      const hasGlobalWorkspaceAccess = projectWorkspaces.some((w) => w.isGlobal);
      const hasDefaultWorkspaceAccess = projectWorkspaces.some((w) => w.isDefault);
      const selectedWorkspace: WorkspaceObj = projectWorkspaces.find((w: WorkspaceObj) => w.id === workspaceId)!;
      mixpanelTrack(TrackingEvent.WorkspaceSelected, {
        'Search Filter': projectSearchFilter,
        'Selected Project ID': projectId,
        'Selected Workspace ID': workspaceId,
        'Selected Workspace Name': i18n.print(item.label),
        'Selected Workspace Is Global': selectedWorkspace.isGlobal,
        'Selected Workspace Is Default': selectedWorkspace.isDefault,
        'User Has Global Workspace Access': hasGlobalWorkspaceAccess,
        'User Has Default Workspace Access': hasDefaultWorkspaceAccess,
        'Total Available Workspaces': projectWorkspaces.length,
        // track url pattern e.g /report/<pid>/view/<wid>/insights. remove trailing slashes
        'Navigate Url Pattern': navigatePath
          .replace(projectId, `<pid>`)
          .replace(workspaceId, `<wid>`)
          .replace(/\/$/, ``),
      });
    } else {
      mixpanelTrack(TrackingEvent.ProjectSelected, {
        'Search Filter': projectSearchFilter,
        'Selected Project ID': projectId,
        'Selected Project Name': i18n.print(item.label),
        'Selected Project Type': item.type === ProjectType.Rollup ? `Rollup Project` : `Project`,
        // track url pattern e.g /report/<pid>/insights. remove trailing slashes
        'Navigate Url Pattern': navigatePath.replace(projectId, `<pid>`).replace(/\/$/, ``),
      });
    }

    // This logic only applies when we are explicitly switching projects and not report types,
    // so we keep it out of helpers.navigate to avoid an adding another argument to a complex function.
    // we only want to switch domains for staging and prod i.e (x.)?mixpanel.com domains,
    // not for devboxes or localhost standalone mode
    const curDomain = location.hostname;
    const isMixpanelDotCom = (domain: string) => /\bmixpanel\.com$/.test(domain);
    if (isMixpanelDotCom(curDomain) && projectDomain && projectDomain !== curDomain) {
      window.location.assign(`//` + projectDomain + navigatePath);
    } else {
      navigateTo(navigatePath, ev);
    }
  }

  function handleSelectItem(ev: CustomEvent): void {
    const item = ev.detail.item as SectionItem;
    if (item.projectId) {
      navigateToProject(ev);
    }
    if (item.href || item.onClick) {
      // track, call onClick, and navigate in that order,
      // so that all three can be done if need be
      if (item.trackEvent) {
        mixpanelTrack(item.trackEvent, item.trackProps || {}, {prefix: ``});
      }
      if (item.onClick) {
        item.onClick();
      }
      if (item.href) {
        navigateTo(item.href, ev, {fromAnchorlink: true});
      }
    }
  }

  function handleMenuChange(ev: MenuChangeEvent): void {
    if (ev.detail.isOpen && currentProject) {
      setSelectedProjectWorkspaces(getWorkspacesForCurrentProject({currentProject}));
    }
    props.onMenuChange?.(ev);
    setIsMenuOpen(ev.detail.isOpen);
  }

  const handleSearchFilterChange = React.useCallback(
    debounce((ev: SearchFilterChangeEvent): void => {
      setProjectSearchFilter(ev.detail.searchFilter);
    }, 250),
    [],
  );

  function getCurrentWorkspace(): WorkspaceObj | undefined {
    const currentWorkspaceId = getCurrentWorkspaceId({currentProject});
    return currentWorkspaceId ? currentProject?.workspaces[currentWorkspaceId] : undefined;
  }

  function getCurrentProjectName(): string {
    return !currentProject ? `Loading ...` : currentProject.name;
  }

  function getProjectAndWorkspaceName(truncated: boolean): string {
    const projectName = getCurrentProjectName();
    const workspaceName = areWorkspacesActivated(currentProject?.id) && (getCurrentWorkspace()?.name || ``);
    const separator = ` · `;

    if (truncated) {
      const maxCharLen = 46;
      if (workspaceName) {
        const truncatedWorkspaceName = truncateMiddle(workspaceName, maxCharLen / 2);
        const remainingLen = maxCharLen - truncatedWorkspaceName.length - separator.length;
        const truncatedProjectName = truncateMiddle(projectName, remainingLen);
        return truncatedProjectName + separator + truncatedWorkspaceName;
      }
      return truncateMiddle(projectName, maxCharLen);
    }
    return workspaceName ? projectName + separator + workspaceName : projectName;
  }

  function getProjectMenuLabel(): string {
    if (!currentProject) {
      return `Projects`;
    }
    return getCurrentProjectName();
  }

  function orgHasPerm(org: Organization | undefined, perm: string): boolean {
    if (!org || !perm) {
      return false;
    }
    return Array.isArray(org.permissions) && org.permissions.includes(perm);
  }

  function getUserHasCreateWorkspacePermission(): boolean {
    const projectId = selectedProjectWorkspaces[0].projectId;
    const project = projectsResource.getOne(projectId).value;
    try {
      return Boolean(
        project && orgHasPerm(currentUser?.organizations[project.organizationId], Permission.ManageWorkspaces),
      );
    } catch (err) {
      reporter.error(err, {projectId, project, orgIds: getOrgs().map((org) => org.id)});
      return false;
    }
  }

  function showError(_errorAlertMsg: string, err: Error): void {
    if (err) {
      console.error(err);
    }
    setErrorAlertMsg(_errorAlertMsg);
  }

  async function handleCreateWorkspaceBtnClick(): Promise<void> {
    // grab this now because the menu can collapse behind the modal and will 'reset' to the current project based on attrs
    const projectId = selectedProjectWorkspaces[0].projectId;
    try {
      mixpanelTrack(TrackingEvent.CreateDataViewClicked);
      const {didSave, name, description} = await promptManageWorkspaceModal(
        i18n.t(`settings.project-settings.data-views.create-new`),
        {},
        projectId,
      );
      if (didSave) {
        const workspace = await workspacesClient.createWorkspace(projectId, {name, description});
        projectsResource.update({
          [projectId]: {
            workspaces: {[workspace.id]: workspace},
          },
        });
        if (projectId === currentProject?.id) {
          currentProjectResource.update({
            workspaces: {[workspace.id]: workspace},
          });
        }
        const projectWorkspaces = getWorkspacesForProject({projectsResource, currentProject, projectId});
        if (selectedProjectWorkspaces[0] && selectedProjectWorkspaces[0].projectId === projectId) {
          // the already computed and sorted project menu needs to be updated with the new workspace
          setSelectedProjectWorkspaces(projectWorkspaces);
        }
        const [eventName, eventProps] = workspaceCreateEvent(
          projectWorkspaces.length,
          {
            name,
            description,
            projectId,
          },
          UISourceObject.ChromeHeaderCreateWorkspace,
        );
        mixpanelTrack(`${workspacesTrackingPrefix} ${eventName}`, eventProps, {prefix: ``});
        addToast({
          message: `Workspace ${name} Created`,
          type: ToastType.SUCCESS,
          cta: `Manage`,
          ctaHref: `/report/${workspace.projectId}/settings/#project/${workspace.projectId}/views/detail/${workspace.id}`,
        });
      }
    } catch (err) {
      const [eventName, eventProps] = workspaceApiFailure(ApiFailureDescription.Creation, err.message, {
        projectId,
        source: UISourceObject.ChromeHeaderCreateWorkspace,
      });
      mixpanelTrack(`${workspacesTrackingPrefix} ${eventName}`, eventProps, {prefix: ``});
      if (err.message && (err.message as string).includes(`already exists`)) {
        addToast({
          message: `Data View already exists`,
          type: ToastType.ERROR,
          cta: `Manage Data Views`,
          ctaHref: `/settings/project/${projectId}/views`,
        });
      } else {
        showError(`Data view creation failed. Please try again or contact support.`, err);
      }
    }
  }

  function hasOrgs(): boolean {
    return !isEmpty(currentUser?.organizations);
  }

  function getOrgsWithCreateProjectPermission(): Organization[] {
    return getOrgs({permissions: [Permission.CreateProject]});
  }

  function handleClickCreateProject(): void {
    setIsMenuOpen(false);
    mixpanelTrack(TrackingEvent.CreateProjectClicked);
    props.onClickCreateProject();
  }

  function getCollapsedTooltipText(): string {
    let tooltipText = getProjectMenuLabel();
    const currentWorkspace = getCurrentWorkspace();
    if (currentWorkspace) {
      tooltipText = `${tooltipText}\n${currentWorkspace.name}`;
    }
    return tooltipText;
  }

  const displayWorkspaceSelection = areWorkspacesActivated();
  const projectsLength = getProjectsForMenu().length;
  const currentWorkspace = getCurrentWorkspace();

  return (
    <div className={styles.container}>
      <SelectReact
        key={`select-collapsed:${props.isCollapsedMode}`}
        elref='projectSwitcher'
        className={clsx(styles.select, {[styles.active]: isMenuOpen})}
        open={isMenuOpen}
        theme={SelectTheme.Tertiary}
        searchPlaceholder={
          displayWorkspaceSelection ? i18n.t(`nav-header.search-data-views`) : i18n.t(`nav-header.search-projects`)
        }
        menuWidth={300}
        menuHeight={400}
        backButton={displayWorkspaceSelection}
        headerTitle={
          displayWorkspaceSelection
            ? `${i18n.t(`nav-header.data-views`)} (${selectedProjectWorkspaces.length})`
            : `${i18n.t(`nav-header.projects`)} (${projectsLength})`
        }
        sectionsFn={() => getMenuSections()}
        onBackClick={() => handleBackClick()}
        onChange={(ev) => handleSelectItem(ev as ChangeEvent)}
        onMenuChange={handleMenuChange}
        onSearchFilterChange={handleSearchFilterChange}
        menuAlignment={props.isCollapsedMode ? Directions.Right : Directions.Down}
        iconOnly={props.isCollapsedMode}
        icon={props.isCollapsedMode ? {leftIcon: `mixpanel-logo`} : undefined}
        neverShowCaret
        tooltip={
          props.isCollapsedMode
            ? {
                placement: Placement.RightCenter,
                text: getCollapsedTooltipText(),
                size: DisplaySize.Fit,
                'text-align': TooltipTextAlign.Start,
              }
            : undefined
        }
      >
        {!props.isCollapsedMode && (
          <div className={styles.labelContainer} slot={`trigger`}>
            <SvgIconReact className={styles.logoIcon} icon={`mixpanel-logo`} />
            <div className={styles.label} title={getProjectAndWorkspaceName(/*truncated*/ false)}>
              <>
                <div className={styles.labelProject}>{getProjectMenuLabel()}</div>
                {currentWorkspace && <div className={styles.labelWorkspace}>{currentWorkspace.name}</div>}
              </>
            </div>
            <SvgIconReact className={styles.caretIcon} icon={`caret-down`} />
          </div>
        )}
        {displayWorkspaceSelection ? (
          getUserHasCreateWorkspacePermission() ? (
            <div className={styles.add} slot={`footer`}>
              <ButtonBarReact
                className={styles.addBtn}
                theme={ButtonBarTheme.Secondary}
                onClick={() => handleCreateWorkspaceBtnClick()}
              >
                <div>{i18n.t(`nav-header.create-data-view`)}</div>
              </ButtonBarReact>
            </div>
          ) : null
        ) : (
          // do not show add project dialog for org members unless they have perms
          <div className={styles.add} slot={`footer`}>
            {hasOrgs() && (!getOrgs()[0].id || getOrgsWithCreateProjectPermission().length) ? (
              <ButtonBarReact
                className={styles.addBtn}
                theme={ButtonBarTheme.Secondary}
                onClick={() => handleClickCreateProject()}
              >
                <div>{i18n.t(`nav-header.create-project`)}</div>
              </ButtonBarReact>
            ) : null}
          </div>
        )}
      </SelectReact>
      {errorAlertMsg && (
        <ToastPortalReact
          toast={{
            isStatic: true,
            type: ToastType.ERROR,
            message: errorAlertMsg,
            closeToastCallback: () => setErrorAlertMsg(undefined),
          }}
        />
      )}
    </div>
  );
}, css);
